"use client";

/**
 * Tile-level error and empty-state primitives.
 *
 * Two patterns:
 *
 *   <TileErrorBoundary>          React error boundary — catches render
 *     <SomeCard/>                exceptions inside a tile and shows a
 *   </TileErrorBoundary>         subtle fallback instead of taking the
 *                                whole dashboard down.
 *
 *   <TileError                   Inline "this tile failed to load" state
 *     reason="network"           for hooks (react-query isError). The
 *     onRetry={refetch}/>        message is professional, the visual
 *                                is dezent — light border, no red panic.
 *
 * Anti-pattern: never use a giant alarm-red banner for a single chart
 * that didn't load. The dashboard is a panel of independent tiles;
 * one tile failing should look "this one is still warming up" — not
 * "the system is on fire". Reserve the loud failure UI for the full
 * page error state ("alles geht nicht").
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

// ── Subtle inline error UI ────────────────────────────

export interface TileErrorProps {
  /** Short user-facing reason. Keep it under 60 chars. */
  message?: string;
  /** Optional retry handler. Omit on tiles that can't usefully retry. */
  onRetry?: () => void;
  /** Visible label on the retry control. Defaults to "Retry". */
  retryLabel?: string;
  /** Full-tile (use as standalone card body) vs inline (slot inside an existing card). */
  variant?: "card" | "inline";
}

export function TileError({
  message = "Could not load.",
  onRetry,
  retryLabel = "Retry",
  variant = "card",
}: TileErrorProps) {
  const base =
    variant === "card"
      ? "flex h-full min-h-[120px] flex-col items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-8 text-center"
      : "flex items-center justify-center gap-2 px-4 py-6 text-center";

  return (
    <div className={base} role="status">
      <svg
        aria-hidden
        className="h-5 w-5 text-text-tertiary"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
        />
      </svg>
      <p className="text-xs text-text-secondary">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 rounded-md border border-border bg-surface-hover px-2.5 py-1 text-[11px] font-medium text-text-secondary transition-colors hover:bg-accent/10 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
        >
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}

// ── React error boundary ─────────────────────────────

interface TileErrorBoundaryProps {
  children: ReactNode;
  /** Override the fallback message. */
  fallbackMessage?: string;
  /** Optional callback when an error is caught (e.g. for telemetry). */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface TileErrorBoundaryState {
  errored: boolean;
}

export class TileErrorBoundary extends Component<
  TileErrorBoundaryProps,
  TileErrorBoundaryState
> {
  override state: TileErrorBoundaryState = { errored: false };

  static getDerivedStateFromError(): TileErrorBoundaryState {
    return { errored: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the error to the dev console so it shows up in HMR.
    // Production telemetry hook is left as an opt-in via ``onError``.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[TileErrorBoundary] caught:", error, info);
    }
    this.props.onError?.(error, info);
  }

  private reset = () => {
    this.setState({ errored: false });
  };

  override render() {
    if (this.state.errored) {
      return (
        <TileError
          message={
            this.props.fallbackMessage ??
            "This tile could not be displayed."
          }
          onRetry={this.reset}
          retryLabel="Reload"
        />
      );
    }
    return this.props.children;
  }
}
